import React, { useState, useEffect, useRef } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Spinner,
} from "reactstrap"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import NewsAndArticleApi from "Api/NewsAndArticleApi"
import S3UploadApi from "Api/s3Upload"
import { Toast } from "primereact/toast"
import { Editor } from "primereact/editor"
import "./EditNewsAndArticle.scss"
import { InputText } from "primereact/inputtext"

const EditNewsAndArticle = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const toast = useRef(null)
  const [loading, setLoading] = useState(false)
  const [articleData, setArticleData] = useState({
    title: "",
    content: "",
    image: "",
    name: " ",
    position: "",
  })
  const [errors, setErrors] = useState({})
  const [imageFile, setImageFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const accessToken = localStorage.getItem("accessToken")

  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "News and Articles", link: "#" },
    { label: "Edit Article", link: "#" },
  ]

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await NewsAndArticleApi.getNewsAndArticleById(id)
        if (response.data.success) {
          setArticleData(prev => ({
            ...prev,
            title: response.data.data.title,
            content: response.data.data.content,
            image: response.data.data.image,
            name: response.data.data.authorDetails.name,
            position: response.data.data.authorDetails.position,
          }))
        } else {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: response.data.message || "Failed to fetch article",
            life: 3000,
          })
        }
      } catch (error) {
        console.error("Error fetching article:", error)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to fetch article",
          life: 3000,
        })
      }
    }
    fetchArticle()
  }, [id])

  const handleInputChange = e => {
    const { name, value } = e.target
    setArticleData(prev => ({
      ...prev,
      [name]: value,
    }))
    setErrors(prev => ({
      ...prev,
      [name]: "",
    }))
  }

  const handleEditorChange = html => {
    setArticleData(prev => ({
      ...prev,
      content: html,
    }))
    setErrors(prev => ({
      ...prev,
      content: "",
    }))
  }

  const handleImageChange = e => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0])
    }
  }

  const uploadImage = async () => {
    if (!imageFile) return null

    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append("image", imageFile)

      const response = await S3UploadApi.uploadImage(formData)
      const newImageUrl = response.data.imageUrl

      if (articleData.image) {
        await deleteImage(articleData.image)
      }

      setArticleData(prev => ({
        ...prev,
        image: newImageUrl,
      }))
      setImageFile(null)
      setIsUploading(false)
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Image uploaded successfully",
        life: 3000,
      })
      return newImageUrl
    } catch (error) {
      console.error("Error uploading image:", error)
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to upload image",
        life: 3000,
      })
      setIsUploading(false)
      return null
    }
  }

  const deleteImage = async imageUrl => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }

      await S3UploadApi.deleteImage({ fileUrl: imageUrl }, config)
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Image deleted successfully",
        life: 3000,
      })
      return true
    } catch (error) {
      console.error("Error deleting image:", error)
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete image",
        life: 3000,
      })
      return false
    }
  }

  const handleRemoveImage = async () => {
    if (articleData.image) {
      const success = await deleteImage(articleData.image)
      if (success) {
        setArticleData(prev => ({
          ...prev,
          image: "",
        }))
        setImageFile(null)
        const fileInput = document.getElementById("image")
        if (fileInput) {
          fileInput.value = ""
        }
      }
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (!articleData.title.trim()) {
      newErrors.title = "Title is required"
    }
    if (!articleData.content.trim()) {
      newErrors.content = "Content is required"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!validateForm()) return

    setLoading(true)
    try {
      let finalImageUrl = articleData.image

      if (imageFile) {
        const uploadedImageUrl = await uploadImage()
        if (uploadedImageUrl) {
          finalImageUrl = uploadedImageUrl
        } else {
          toast.current.show({
            severity: "warn",
            summary: "Warning",
            detail: "Failed to upload new image. Please try again.",
            life: 3000,
          })
          setLoading(false)
          return
        }
      }

      const payload = {
        title: articleData.title,
        content: articleData.content,
        authorDetails: {
          name: articleData.name,
          position: articleData.position,
        },
        image: finalImageUrl || "",
      }

      const response = await NewsAndArticleApi.updateNewsAndArticle(id, payload)

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Article updated successfully",
        life: 3000,
      })

      setTimeout(() => {
        navigate("/news-and-articles")
      }, 1000)
    } catch (error) {
      console.error("Error updating article:", error)
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to update article",
        life: 3000,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="edit-news-article">
      <Toast ref={toast} position="top-right" />
      <Container>
        <Row>
          <Col xs="12">
            <div className="edit-article-header d-flex align-items-center justify-content-between mb-4">
              <CustomBreadCrumb
                data={breadcrumbItems}
                pageTitle="Edit Article"
              />
            </div>

            <Card>
              <CardBody>
                <form onSubmit={handleSubmit}>
                  <div className="form-group mb-3">
                    <label htmlFor="title">Title</label>
                    <input
                      type="text"
                      className={`form-control ${errors.title ? "is-invalid" : ""}`}
                      id="title"
                      name="title"
                      value={articleData.title}
                      onChange={handleInputChange}
                    />
                    {errors.title && (
                      <div className="invalid-feedback">{errors.title}</div>
                    )}
                  </div>

                  <div className="d-flex gap-2">
                    <div className="mb-4 flex-grow-1">
                      <label htmlFor="title" className="form-label">
                        Author
                      </label>
                      <InputText
                        id="title"
                        name="name"
                        value={articleData.name}
                        onChange={handleInputChange}
                        className="w-100"
                        placeholder="Enter author name"
                      />
                    </div>

                    <div className="mb-4 flex-grow-1">
                      <label htmlFor="title" className="form-label">
                        Position
                      </label>
                      <InputText
                        id="title"
                        value={articleData.position}
                        onChange={handleInputChange}
                        name="position"
                        className="w-100"
                        placeholder="Enter author position"
                      />
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label htmlFor="image">Image</label>
                    <div className="inp-err">
                      {articleData.image && (
                        <div className="mb-2 position-relative">
                          <img
                            src={articleData.image}
                            alt="Article"
                            style={{
                              width: "200px",
                              height: "auto",
                              objectFit: "contain",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              padding: "2px",
                            }}
                          />
                          <Button
                            type="button"
                            color="danger"
                            size="sm"
                            className="position-absolute top-0 end-0"
                            style={{ margin: "2px" }}
                            onClick={handleRemoveImage}
                          >
                            ×
                          </Button>
                        </div>
                      )}
                      {imageFile && (
                        <div className="mb-2">
                          <small>Selected file: {imageFile.name}</small>
                        </div>
                      )}
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="file"
                          className={`form-control ${errors.image ? "is-invalid" : ""}`}
                          id="image"
                          accept="image/*"
                          onChange={handleImageChange}
                          disabled={isUploading}
                        />
                        {imageFile && !isUploading && (
                          <Button
                            type="button"
                            color="primary"
                            className="d-flex align-items-center h-100"
                            onClick={uploadImage}
                          >
                            Upload
                          </Button>
                        )}
                      </div>
                      {isUploading && (
                        <small className="d-block mt-1">
                          <Spinner size="sm" /> Uploading image...
                        </small>
                      )}
                      {errors.image && (
                        <div className="invalid-feedback">{errors.image}</div>
                      )}
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label htmlFor="content">Content</label>
                    <Editor
                      value={articleData.content}
                      onTextChange={e => handleEditorChange(e.htmlValue)}
                      style={{ height: "320px" }}
                      className={errors.content ? "is-invalid" : ""}
                    />
                    {errors.content && (
                      <div className="invalid-feedback">{errors.content}</div>
                    )}
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      type="button"
                      color="secondary"
                      onClick={() => navigate("/news-and-articles")}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" color="primary" disabled={loading}>
                      {loading ? "Updating..." : "Update Article"}
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default EditNewsAndArticle
